/**
 * The Gantt, as arithmetic: which rows there are, what span each one covers,
 * where the window starts and ends, and where the ticks fall.
 *
 * React-free and DOM-free on purpose, with `ganttModel.test.ts` beside it
 * (AGENTS.md §4). Everything in this file is a RULE rather than a rendering, and
 * the rules are the part that is easy to get quietly wrong: a milestone with no
 * start date, a task whose due date is before its start, a project whose only
 * dated thing is a deadline in 2019. None of those can be tested through a
 * component, and all of them exist in the imported data today.
 *
 * The one import is type-only, so nothing here pulls the api client - the same
 * arrangement `listView.ts` uses to stay loadable under the node test runner.
 *
 * ── WHY A ROW CAN HAVE NO SPAN ─────────────────────────────────────────────
 *
 * Because most rows in this CRM do not have two dates, and pretending otherwise
 * is the failure mode of every Gantt bolted onto an existing dataset. The old
 * system recorded a deadline and nothing else, so `Milestone.startDate` is null
 * on every imported row and a great many tasks carry a due date alone.
 *
 * Three answers, and the difference between them matters:
 *   - Two dates: the bar is what was planned.
 *   - One date: the bar is a MARKER on that day, flagged (`assumedStart` /
 *     `assumedEnd`) so the chart can draw it differently. Inventing a two-week
 *     bar backwards from a deadline would be the chart making up a plan.
 *   - No dates: no bar at all. The row is left out of the chart and COUNTED, so
 *     the screen can say "and 12 tasks with no dates" instead of showing a
 *     half-empty timeline that looks like the whole project.
 *
 * A milestone with no dates of its own but with dated tasks under it is the
 * fourth case and gets a `derived` span covering its children - a summary bar,
 * which is what a phase actually is. That is a fact about the tasks, not an
 * invention, so it is allowed where a made-up duration is not.
 */
import type { Milestone, Project, Task, TaskStatus } from "../../../../lib/projects";

/** How much calendar one column of the chart covers. */
export const GANTT_SCALES = ["day", "week", "month"] as const;

export type GanttScale = (typeof GANTT_SCALES)[number];

/** What a row IS, which decides its shape and its colour, never its indent. */
export type GanttRowKind = "project" | "milestone" | "task";

/**
 * The stretch of calendar a row covers, in epoch milliseconds.
 *
 * `endMs` is the END of the last day, not its start: a task due on the 5th
 * occupies the 5th, and a bar that stopped at midnight on the 5th would be drawn
 * as covering the 4th. Every bar in this file is a half-open interval
 * `[startMs, endMs)` over whole local days for exactly that reason.
 */
export interface GanttSpan {
  startMs: number;
  endMs: number;
  /** The row had only an end date. The bar is a marker, not a plan. */
  assumedStart: boolean;
  /** The row had only a start date. Same. */
  assumedEnd: boolean;
  /** A milestone bar computed from its tasks because the phase itself has no
   *  dates. True summary bar, not a guess. */
  derived: boolean;
}

export interface GanttRow {
  id: string;
  kind: GanttRowKind;
  label: string;
  /** 0 for the project and the phases, 1 for a task under a phase. Purely the
   *  indent - what a row IS lives in `kind`. */
  depth: 0 | 1;
  /** Null only on the synthetic "no phase" heading, which has no dates of its
   *  own and is never drawn as a bar. */
  span: GanttSpan | null;
  done: boolean;
  /** Not finished and its end date is behind us. Computed against the `now` the
   *  caller passes rather than `Date.now()`, so the tests are not a clock. */
  late: boolean;
  /** The task's own status, for the dot beside the label. Null on the other two
   *  kinds, which have no task status to report. */
  status: TaskStatus | null;
}

export interface GanttModel {
  rows: GanttRow[];
  /** Tasks left out because they carry no date at all. The screen must say this
   *  number out loud - a chart quietly missing a third of the work reads as the
   *  whole project. */
  undatedTasks: number;
  /** Phases left out for the same reason: no dates of their own AND no dated
   *  task under them to derive a bar from. */
  undatedMilestones: number;
}

/** One label on the time axis. */
export interface GanttTick {
  key: string;
  label: string;
  startMs: number;
  endMs: number;
  /** The tick that contains `now`. Drawn differently; also what the today line
   *  is checked against in the tests. */
  current: boolean;
}

/**
 * Two rows of labels, because one is not readable.
 *
 * A day scale showing only "1 2 3 4 5" tells you nothing about which month you
 * are looking at, and a month scale showing only "jaan veebr" loses the year the
 * moment the project crosses a December. So the minor row carries the unit the
 * bars are measured in and the major row carries the one above it.
 */
export interface GanttAxis {
  major: GanttTick[];
  minor: GanttTick[];
}

export interface GanttWindow {
  startMs: number;
  endMs: number;
}

const DAY_MS = 86_400_000;

/** Dates are printed in Estonian everywhere in this app regardless of the UI
 *  language, the same decision `crm/ui/format.ts` records for the same reason. */
const AXIS_LOCALE = "et-EE";

/**
 * How wide one minor column wants to be, in pixels.
 *
 * The chart positions everything in percentages, so these numbers do not place
 * anything - they set the canvas `min-width` and therefore decide when the chart
 * starts scrolling sideways instead of squeezing. A day column narrower than
 * this cannot fit a two-digit date; a month column narrower than this cannot fit
 * a short month name.
 */
export const GANTT_UNIT_PX: Record<GanttScale, number> = {
  day: 34,
  week: 58,
  month: 92,
};

// ── Calendar arithmetic, all in LOCAL time ────────────────────────────────
//
// Local and not UTC, because every date in this product is a day somebody chose
// in their own calendar: `toDateInput` in `../lib.ts` builds `yyyy-mm-dd` out of
// local parts precisely so a deadline does not shift by a day for half the year.
// A Gantt built on UTC boundaries would put a bar in the wrong column for
// everything created between midnight and 03:00 in summer time.

export function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function addDays(ms: number, n: number): number {
  const d = new Date(ms);
  d.setDate(d.getDate() + n);
  // Re-normalised rather than `ms + n * DAY_MS`: adding 86 400 000 across a
  // daylight-saving boundary lands at 23:00 or 01:00 of the right day, and every
  // comparison afterwards is then off by one column twice a year.
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Monday. Estonian weeks start on Monday and so does every calendar in this
 *  product; `getDay()` calls Sunday 0, which is the off-by-one this converts. */
export function startOfWeek(ms: number): number {
  const d = new Date(startOfDay(ms));
  const shift = (d.getDay() + 6) % 7;
  return addDays(d.getTime(), -shift);
}

export function startOfMonth(ms: number): number {
  const d = new Date(ms);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function addMonths(ms: number, n: number): number {
  const d = new Date(startOfMonth(ms));
  d.setMonth(d.getMonth() + n);
  return d.getTime();
}

/** The start of the unit this scale measures in. */
function unitStart(ms: number, scale: GanttScale): number {
  if (scale === "month") return startOfMonth(ms);
  if (scale === "week") return startOfWeek(ms);
  return startOfDay(ms);
}

/** The start of the NEXT unit. */
function unitNext(ms: number, scale: GanttScale): number {
  if (scale === "month") return addMonths(ms, 1);
  if (scale === "week") return addDays(unitStart(ms, "week"), 7);
  return addDays(startOfDay(ms), 1);
}

/**
 * An ISO instant as a local day start, or null.
 *
 * Null for an absent value AND for an unparseable one. A string the browser
 * cannot read produces `NaN`, and `NaN` propagates silently through every
 * comparison below - a single bad row would move the window to the beginning of
 * time and squash every bar into one pixel.
 */
function dayOf(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? startOfDay(ms) : null;
}

/**
 * Turn a pair of optional dates into a span.
 *
 * The end is exclusive - `addDays(end, 1)` - so a one-day task covers its day
 * rather than a hairline at midnight. See `GanttSpan`.
 *
 * An end BEFORE the start is not thrown away and not swapped. It is clamped to
 * the start, which draws a one-day marker on the start date. Swapping would
 * silently invent a plan nobody typed; dropping the row would hide a data error
 * that the operator is the only person able to fix.
 */
export function spanOf(startIso: string | null, endIso: string | null): GanttSpan | null {
  const rawStart = dayOf(startIso);
  const rawEnd = dayOf(endIso);
  if (rawStart === null && rawEnd === null) return null;

  if (rawStart !== null && rawEnd !== null) {
    const end = Math.max(rawEnd, rawStart);
    return { startMs: rawStart, endMs: addDays(end, 1), assumedStart: false, assumedEnd: false, derived: false };
  }
  if (rawStart !== null) {
    return { startMs: rawStart, endMs: addDays(rawStart, 1), assumedStart: false, assumedEnd: true, derived: false };
  }
  const end = rawEnd as number;
  return { startMs: end, endMs: addDays(end, 1), assumedStart: true, assumedEnd: false, derived: false };
}

/** The smallest span covering all of them, or null if there are none. */
function coverOf(spans: readonly GanttSpan[]): GanttSpan | null {
  // Seeded from nothing and widened by the loop, rather than from `spans[0]`.
  // Not style: this project compiles with `noUncheckedIndexedAccess`, so an
  // index into an array whose length was checked one line earlier is still
  // `GanttSpan | undefined` to the type checker - and the guard the compiler
  // would make you write is a guard that can never fire.
  let startMs: number | null = null;
  let endMs: number | null = null;
  for (const s of spans) {
    if (startMs === null || s.startMs < startMs) startMs = s.startMs;
    if (endMs === null || s.endMs > endMs) endMs = s.endMs;
  }
  if (startMs === null || endMs === null) return null;
  return { startMs, endMs, assumedStart: false, assumedEnd: false, derived: true };
}

/** A row is late when it is unfinished and its last day is already behind us. */
function isLate(span: GanttSpan | null, done: boolean, nowMs: number): boolean {
  if (done || !span) return false;
  return span.endMs <= startOfDay(nowMs);
}

/** What the board hands over: a phase and the tasks filed under it. The unfiled
 *  column carries a null milestone, which is how it is told apart. */
export interface GanttColumn {
  milestone: Milestone | null;
  tasks: readonly Task[];
}

/**
 * Every row the chart draws, in the order it draws them.
 *
 * The project first, then each phase with its tasks indented under it, then the
 * tasks belonging to no phase. That is the board's own order, so a project reads
 * the same top to bottom on both screens.
 *
 * `unfiledLabel` is passed in rather than looked up, because this file must stay
 * free of the i18n provider to remain testable - the caller already has `t`.
 */
export function buildGanttModel(
  project: Pick<Project, "id" | "name" | "status" | "startDate" | "deadline">,
  columns: readonly GanttColumn[],
  nowMs: number,
  unfiledLabel: string,
): GanttModel {
  const rows: GanttRow[] = [];
  let undatedTasks = 0;
  let undatedMilestones = 0;

  const projectDone = project.status === "completed";
  const projectSpan = spanOf(project.startDate, project.deadline);
  rows.push({
    id: `project:${project.id}`,
    kind: "project",
    label: project.name,
    depth: 0,
    // Drawn even when it is a bare deadline marker, and drawn even when it is
    // null. The project row is the one row that must always be listed: its
    // absence from the chart is itself the answer to "why does this timeline
    // look empty", and a project with no dates at all is a real and common
    // state that the operator can fix from the edit dialog.
    span: projectSpan,
    done: projectDone,
    late: isLate(projectSpan, projectDone, nowMs),
    status: null,
  });

  for (const col of columns) {
    const taskRows: GanttRow[] = [];
    for (const task of col.tasks) {
      const span = spanOf(task.startDate ?? null, task.dueDate ?? null);
      if (!span) {
        undatedTasks += 1;
        continue;
      }
      const done = task.status === "done" || !!task.completedAt;
      taskRows.push({
        id: `task:${task.id}`,
        kind: "task",
        label: task.title,
        depth: 1,
        span,
        done,
        late: isLate(span, done, nowMs),
        status: task.status,
      });
    }

    if (col.milestone) {
      const m = col.milestone;
      const own = spanOf(m.startDate, m.dueDate);
      // Its own dates win over the derived cover. A phase that says it runs to
      // the 30th runs to the 30th even if somebody filed a task due on the 5th
      // of the next month under it - the plan is the plan, and the overrunning
      // task is exactly the thing the chart should show sticking out past it.
      const span = own ?? coverOf(taskRows.map((r) => r.span as GanttSpan));
      if (!span) {
        // No dates of its own and no dated task to borrow from. Counted and left
        // out, along with the tasks under it, which were counted already.
        undatedMilestones += 1;
        continue;
      }
      rows.push({
        id: `milestone:${m.id}`,
        kind: "milestone",
        label: m.name,
        depth: 0,
        span,
        done: m.done,
        late: isLate(span, m.done, nowMs),
        status: null,
      });
      rows.push(...taskRows);
      continue;
    }

    // The unfiled column. It gets a heading only when it has something to show,
    // and the heading carries no bar - "no phase" is not a period of time.
    if (taskRows.length === 0) continue;
    rows.push({
      id: "milestone:none",
      kind: "milestone",
      label: unfiledLabel,
      depth: 0,
      span: null,
      done: false,
      late: false,
      status: null,
    });
    rows.push(...taskRows);
  }

  return { rows, undatedTasks, undatedMilestones };
}

/**
 * How much calendar the chart shows.
 *
 * Snapped OUT to whole units on both sides, so the first and last columns are
 * full ones - a chart whose left edge cuts a week in half puts every bar a
 * fraction out of line with the tick above it.
 *
 * Today is always inside the window. A project that finished last year would
 * otherwise draw a today line off the edge, or none at all, and the operator
 * loses the one reference point that tells them whether the plan is behind. The
 * cost is a long empty stretch on old projects, which reads correctly: that gap
 * IS the time since the work stopped.
 *
 * A project with no dated row anywhere gets a window around today rather than
 * nothing, so the axis still draws and the screen shows an empty calendar rather
 * than a broken one.
 */
export function ganttWindow(rows: readonly GanttRow[], nowMs: number, scale: GanttScale): GanttWindow {
  const spans = rows.map((r) => r.span).filter((s): s is GanttSpan => !!s);
  const today = startOfDay(nowMs);

  let startMs = today;
  let endMs = addDays(today, 1);
  for (const s of spans) {
    if (s.startMs < startMs) startMs = s.startMs;
    if (s.endMs > endMs) endMs = s.endMs;
  }

  // One unit of air on each side. Without it a bar that starts on the first day
  // of the window is flush against the frame and reads as running off it.
  const padded = unitStart(startMs, scale);
  const start = scale === "month" ? addMonths(padded, -1) : scale === "week" ? addDays(padded, -7) : addDays(padded, -2);

  let end = unitStart(endMs, scale);
  // `endMs` is exclusive, so a span ending exactly on a unit boundary needs no
  // extra column - anything else does, or its last day falls outside the window.
  if (end < endMs) end = unitNext(end, scale);
  end = scale === "month" ? addMonths(end, 1) : scale === "week" ? addDays(end, 7) : addDays(end, 2);

  return { startMs: start, endMs: end };
}

/** Where a moment sits in the window, as a percentage. Clamped, so a bar that
 *  somehow escapes the window is drawn at the edge rather than off-canvas. */
export function offsetPct(ms: number, win: GanttWindow): number {
  const total = win.endMs - win.startMs;
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, ((ms - win.startMs) / total) * 100));
}

/**
 * A bar's left edge and width, in percent of the window.
 *
 * The width is floored at nothing - a zero-width bar is a real outcome for a
 * single-day marker on a year-long window, and it is the stylesheet's job to
 * give it a minimum pixel width. Doing it here in percent would make the floor
 * depend on how much calendar happens to be on screen.
 */
export function barGeometry(span: GanttSpan, win: GanttWindow): { leftPct: number; widthPct: number } {
  const leftPct = offsetPct(span.startMs, win);
  const rightPct = offsetPct(span.endMs, win);
  return { leftPct, widthPct: Math.max(0, rightPct - leftPct) };
}

function monthLabel(ms: number, withYear: boolean): string {
  const d = new Date(ms);
  const month = new Intl.DateTimeFormat(AXIS_LOCALE, { month: "short" }).format(d);
  return withYear ? `${month} ${d.getFullYear()}` : month;
}

/**
 * The two rows of labels over the chart.
 *
 * Minor is the unit the bars are measured in. Major is the unit above it, and it
 * spans as many minor columns as it contains - which is why a major tick carries
 * its own start and end rather than a column count: a month is 28, 30 or 31 days
 * and four or five weeks, and a fixed span would drift by a column a year.
 *
 * A hard cap on the number of minor ticks, because the window is derived from
 * the data and the data can contain a deadline typed as the year 2299. Without
 * it that one row produces a hundred thousand DOM nodes and the tab stops
 * responding - the chart would be unreadable long before that, so the cap costs
 * nothing real.
 */
const MAX_TICKS = 400;

export function ganttAxis(win: GanttWindow, scale: GanttScale, nowMs: number): GanttAxis {
  const today = startOfDay(nowMs);
  const minor: GanttTick[] = [];
  const major: GanttTick[] = [];

  for (let ms = unitStart(win.startMs, scale); ms < win.endMs && minor.length < MAX_TICKS; ) {
    const next = unitNext(ms, scale);
    const d = new Date(ms);
    minor.push({
      key: `m${ms}`,
      label:
        scale === "month"
          ? monthLabel(ms, false)
          : scale === "week"
            ? String(d.getDate())
            : String(d.getDate()),
      startMs: ms,
      endMs: next,
      current: today >= ms && today < next,
    });
    ms = next;
  }

  // Months over days and weeks; years over months. One level up, always.
  if (scale === "month") {
    for (let ms = startOfMonth(win.startMs); ms < win.endMs && major.length < MAX_TICKS; ) {
      const year = new Date(ms).getFullYear();
      const next = new Date(year + 1, 0, 1).getTime();
      const end = Math.min(next, win.endMs);
      major.push({ key: `y${year}`, label: String(year), startMs: ms, endMs: end, current: new Date(today).getFullYear() === year });
      ms = next;
    }
  } else {
    for (let ms = startOfMonth(win.startMs); ms < win.endMs && major.length < MAX_TICKS; ) {
      const next = addMonths(ms, 1);
      const end = Math.min(next, win.endMs);
      major.push({
        key: `M${ms}`,
        // The year is on every major tick rather than only on January, because
        // the window can open in the middle of a year and the first label would
        // then be the only one without one.
        label: monthLabel(ms, true),
        startMs: ms,
        endMs: end,
        current: today >= ms && today < next,
      });
      ms = next;
    }
  }

  return { major, minor };
}

/** How wide the canvas wants to be before it starts scrolling sideways. */
export function ganttCanvasPx(axis: GanttAxis, scale: GanttScale): number {
  return axis.minor.length * GANTT_UNIT_PX[scale];
}

export { DAY_MS };
